/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 테이블 헤더 셀 클래스를 정의한다.
 *
 * @file /src/models/table/Head.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 8. 30.
 */
import { Menu, items as menus } from '@components/menu/Menu';
import * as filters from './Filters';
import { Column } from './Column';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Language, LanguageText } from '@utils/Language';
import { Resizer } from '@utils/Resizer';
import './Head.css';

export class Head {
    /**
     * 컬럼
     */
    column: Column;

    /**
     * 헤더 셀 Dom
     */
    $header: Dom = null;

    /**
     * 컬럼제목 Dom
     */
    $text: Dom = null;

    /**
     * 대체행 컬럼제목 Dom
     */
    $alterText: Dom = null;

    /**
     * 필터
     */
    filter: filters.filter.Filter = null;

    /**
     * 컬럼 메뉴
     */
    menu: Menu = null;

    /**
     * 대체행 필터
     */
    alterFilter: filters.filter.Filter = null;

    /**
     * 대체행 컬럼 메뉴
     */
    alterMenu: Menu = null;

    /**
     * Resizer
     */
    resizer: Resizer = null;

    /**
     * 헤더 셀 클래스를 정의한다.
     *
     * @param column - 컬럼
     */
    constructor(column: any) {
        this.column = column;

        this.filter = this.#buildFilter(column.properties?.filter ?? null, column.dataIndex);
        this.menu = this.#buildMenu(column.dataIndex, this.filter, column.properties?.menus ?? null);

        if (column.alternative !== null) {
            this.alterFilter = this.#buildFilter(
                column.properties?.alternative?.filter ?? null,
                column.alternative.dataIndex ?? null
            );
            this.alterMenu = this.#buildMenu(
                column.alternative.dataIndex ?? null,
                this.alterFilter,
                column.properties?.alternative?.menus ?? null
            );
        }
    }

    /**
     * 컬럼제목 Dom 을 가져온다.
     *
     * @returns 컬럼제목 Dom
     */
    $getText(): Dom {
        this.$text ??= Html.create('div', { 'class': 'text' });
        return this.$text;
    }

    /**
     * 대체행 컬럼제목 Dom 을 가져온다.
     *
     * @returns 대체행 컬럼제목 Dom
     */
    $getAlterText(): Dom {
        this.$alterText ??= Html.create('div', { 'class': 'text' });
        return this.$alterText;
    }

    /**
     * 헤더 셀 Dom 을 가져온다.
     *
     * @returns 헤더 셀 Dom
     */
    $getHeader(): Dom {
        if (this.$header === null) {
            const column = this.column;
            this.$header = Html.create('div', { 'data-model': 'table', 'data-role': 'column' });

            this.$getText().addClass(column.headerAlign).addClass(column.headerVerticalAlign);
            this.$header.append(
                this.#buildTitle(this.$getText(), column.text, column.sortable, column.dataIndex, this.menu)
            );

            if (column.alternative !== null && column.alternative.header === true) {
                this.$getAlterText().addClass(column.headerAlign).addClass(column.headerVerticalAlign);
                this.$header.append(
                    this.#buildTitle(
                        this.$getAlterText(),
                        column.alternative.text,
                        column.alternative.sortable,
                        column.alternative.dataIndex,
                        this.alterMenu
                    )
                );
            }

            if (column.hasColumns() == true) {
                const flexGrow = column.getChildrenFlexGrow();
                if (flexGrow > 0) {
                    const basis = column.getChildrenFlexBasis();
                    this.$header.setStyle('flex-grow', flexGrow);
                    this.$header.setStyle('flex-basis', `calc(${basis.px}px + ${basis.rem}rem)`);
                    this.$header.setStyle('width', `calc(${basis.px}px + ${basis.rem}rem)`);
                }

                const $columns = Html.create('div', { 'data-model': 'table', 'data-role': 'columns' });
                column.getColumns().forEach((childColumn: any) => {
                    $columns.append(column.getTable().getHeader().getHead(childColumn).$getHeader());
                });

                this.$header.append($columns);
            } else {
                this.$header.setAttr('data-index', column.columnIndex.toString());
            }
        }

        return this.$header;
    }

    /**
     * 컬럼제목을 변경한다.
     *
     * @param text - 변경할 컬럼제목
     */
    async setText(text: LanguageText): Promise<void> {
        this.$getText().html(text ?? '');
    }

    /**
     * 대체행 컬럼제목을 변경한다.
     *
     * @param text - 변경할 대체행 컬럼제목
     */
    async setAlterText(text: LanguageText): Promise<void> {
        this.$getAlterText().html(await Language.resolveText(text));
    }

    /**
     * 하위 컬럼이 존재할 경우, 헤더 너비를 업데이트한다.
     */
    updateHeader(): void {
        const column = this.column;
        const flexGrow = column.getChildrenFlexGrow();
        if (flexGrow > 0) {
            const basis = column.getChildrenFlexBasis();
            this.$getHeader().setStyle('flex-grow', flexGrow);
            this.$getHeader().setStyle('flex-basis', `calc(${basis.px}px + ${basis.rem}rem)`);
            this.$getHeader().setStyle('width', `calc(${basis.px}px + ${basis.rem}rem)`);
        } else {
            this.$getHeader().setStyle('flex-grow', null);
            this.$getHeader().setStyle('flex-basis', null);
            this.$getHeader().setStyle('width', null);
        }

        const parent = column.getParent();
        if (parent !== null) {
            column.getTable()?.getHeader()?.getHead(parent)?.updateHeader();
        }
    }

    /**
     * 컬럼 리사이즈를 설정한다.
     */
    setResize(): void {
        const column = this.column;
        if (column.resizable == false) {
            return;
        }

        if (column.hasColumns() == true) {
            column.getColumns().forEach((childColumn: any) => {
                column.getTable().getHeader().getHead(childColumn).setResize();
            });
        } else {
            this.resizer = new Resizer(this.$getHeader(), column.getTable().$getComponent(), {
                directions: [false, true, false, false],
                guidelines: [false, true, false, true],
                scroller: column.getTable().getScroller(),
                minWidth: 50,
                listeners: {
                    start: (_$target, _rect, _position, $guide) => {
                        column.getTable().getHeader().$getHeader().addClass('resizing');
                        $guide.setStyle(
                            'height',
                            `calc(100% - ${
                                column.getTable().$getComponent().getOuterHeight() -
                                column.getTable().$getContent().getOuterHeight()
                            }px)`
                        );
                        $guide.setStyle('top', column.getTable().$getHeader().getOuterHeight() + 'px');
                        column.getTable().getScroller().updatePosition();
                    },
                    resize: (_$target, _rect, position, $guide) => {
                        $guide.setStyle(
                            'height',
                            `calc(100% - ${
                                column.getTable().$getComponent().getOuterHeight() -
                                column.getTable().$getContent().getOuterHeight()
                            }px)`
                        );
                        $guide.setStyle('top', column.getTable().$getHeader().getOuterHeight() + 'px');

                        if (column.freeze == false) {
                            const compEl = column.getTable().$getContent();
                            const right = compEl.getOffset().left + compEl.getOuterWidth();
                            const scroller = column.getTable().getScroller();
                            const dist = right - position.x;

                            if (dist < 80) {
                                scroller.startScroll('x', 1);
                            } else {
                                scroller.stopScroll('x');
                            }
                        }
                    },
                    end: (_$target: Dom, rect: DOMRect) => {
                        column.getTable().getHeader().$getHeader().removeClass('resizing');
                        column.setWidth(rect.width);
                        column.getTable().getScroller().stopScroll('x');
                    },
                    dblclick: () => {
                        let maxWidth = 50;
                        Html.all(
                            'div[data-model=table][data-role=column][data-index="' + column.columnIndex + '"]',
                            column.getTable().getBody().$getBody()
                        ).forEach(($column) => {
                            maxWidth = Math.max(maxWidth, $column.getEl().scrollWidth);
                        });

                        column.setWidth(maxWidth);
                    },
                },
            });
        }
    }

    /**
     * 정렬 및 필터 상태를 업데이트한다.
     */
    update(): void {
        const column = this.column;

        if (column.sortable !== false) {
            const direction = column.getTable().getStore().getSorter(column.dataIndex);
            this.$getText().setAttr('data-sort', direction ?? null);
        }

        if (this.filter !== null) {
            const activeFilter = column.getTable().getStore().getFilter(this.filter.dataIndex);
            this.$getText().toggleClass('filtered', activeFilter !== null);
        }
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        this.menu?.remove();
        this.menu = null;
        this.alterMenu?.remove();
        this.alterMenu = null;
        this.resizer?.destroy();
        this.resizer = null;
        this.filter = null;
        this.alterFilter = null;
        this.$header = null;
        this.$text = null;
        this.$alterText = null;
        this.column = null;
    }

    /**
     * 필터를 구성한다.
     */
    #buildFilter(properties: filters.Properties | null, dataIndex: string | null): filters.filter.Filter | null {
        if (properties === null) {
            return null;
        }

        properties.dataIndex ??= dataIndex;

        const filter = filters.Filters.get(properties);
        filter.setHandler(async (dataIndex, filterValue) => {
            if (dataIndex == null) {
                return;
            }

            const store = this.column.getTable().getStore();
            const current = store.getFilters() ?? {};
            let filters = { ...current };
            if (filterValue == null) {
                if (filters[dataIndex] !== undefined) {
                    delete filters[dataIndex];
                }
                if (Object.keys(filters).length == 0) {
                    filters = null;
                }
            } else {
                filters[dataIndex] = filterValue;
            }

            await store.setFilters(filters);
        });

        return filter;
    }

    /**
     * 메뉴를 구성한다.
     */
    #buildMenu(
        dataIndex: string | null,
        filter: filters.filter.Filter | null,
        items: menus.Properties[] | null
    ): Menu | null {
        let menu: Menu = null;

        if (filter !== null) {
            menu ??= new Menu();
            menu.add(filter.getMenu());
            menu.addEvent('show', () => {
                const height =
                    this.column.getTable().$getContent().getHeight() -
                    (this.column.getTable().getHeader()?.$getHeader().getHeight() ?? 0) -
                    20;
                filter.setMaxHeight(height);
                const filtersObj = this.column.getTable().getStore().getFilters();
                const index = filter.dataIndex ?? dataIndex;
                if (index !== null) {
                    filter.setFilter(filtersObj[index] ?? null);
                }
            });
        }

        if (items !== null) {
            menu?.addMenu('-');
            menu ??= new Menu();
            for (const item of items) {
                menu.addMenu(item as any);
            }
        }

        menu?.setMaxWidth(null);
        menu?.addEvent('show', (menu) => {
            const height =
                this.column.getTable().$getContent().getHeight() -
                (this.column.getTable().getHeader()?.$getHeader().getHeight() ?? 0) -
                20;
            menu.setMaxHeight(height);
        });

        return menu;
    }

    /**
     * 헤더 타이틀 Dom 을 구성한다.
     */
    #buildTitle(
        $text: Dom,
        text: LanguageText,
        sortable: boolean | 'ASC' | 'DESC',
        dataIndex: string | null,
        menu: Menu | null
    ): Dom {
        const $title = Html.create('div', { 'data-model': 'table', 'data-role': 'title' });

        Language.resolveText(text).then((text) => {
            $text.html(text);
            if (sortable !== false) {
                $text.prepend(Html.create('i', { 'data-role': 'sorter' }));
                $title.on('pointerdown', () => {
                    const current = this.column.getTable().getStore().getSorter(dataIndex);
                    const direction =
                        current === null ? (sortable === true ? 'ASC' : sortable) : current == 'ASC' ? 'DESC' : 'ASC';
                    this.column.getTable().getStore().sort(dataIndex, direction);
                });
            }
        });

        $title.append($text);

        if (menu !== null) {
            const $button = Html.create('button', { 'type': 'button', 'data-role': 'menu' });
            $title.append($button);
            menu.setTarget($button);
            menu.addEvent('show', () => {
                $title.addClass('expand');
            });
            menu.addEvent('hide', () => {
                $title.removeClass('expand');
            });
            $button.on('pointerdown', (_, e) => {
                e.stopPropagation();
                if (menu.isShow() == true) {
                    menu.hide();
                } else {
                    menu.show();
                }
            });
        }

        return $title;
    }
}
